// File needs to import `displayDate`, `displayTime` for functionality. Although they are deprecated, the component is kept to maintain functionality.
/* eslint-disable import/no-deprecated */

import React from 'react';

import { differenceInCalendarDays, differenceInMinutes } from 'date-fns';

import { displayDate, displayTime } from './datetime';
import deprecate from './deprecate';
import { useLocale } from './locale';

const getRelativeFormattedDate = (
  date: Date,
  T: ReturnType<typeof useLocale>['T'],
  startDate?: Date
) => {
  deprecate(
    'RelativeDate',
    'RelativeDate',
    '@adsk/alloy-react-datetime/RelativeDate'
  );
  startDate = startDate ?? new Date(Date.now());
  const diffMinutes = differenceInMinutes(startDate, date);
  const diffDays = differenceInCalendarDays(startDate, date);

  if (diffMinutes < 1) {
    return T.translate('MOMENTS_AGO');
  } else if (diffMinutes > 1 && diffMinutes < 60) {
    return T.translate('MINUTES_AGO', { minutes: diffMinutes });
  } else if (diffMinutes >= 60 && diffDays === 0) {
    return T.translate('TODAY_AT', {
      time: displayTime(date, { format: 'short', useBrowserTimeZone: true }),
    });
  } else if (diffDays === 1) {
    return T.translate('YESTERDAY_AT', {
      time: displayTime(date, { format: 'short', useBrowserTimeZone: true }),
    });
  } else {
    return T.translate('DATE_AT_TIME', {
      date: displayDate(date, { format: 'short', useBrowserTimeZone: true }),
      time: displayTime(date, { format: 'short', useBrowserTimeZone: true }),
    });
  }
};

/**
 * Takes two dates and returns the difference between them as a localized relative date
 * @deprecated - please use `@adsk/alloy-react-datetime`
 * @param options - options object can contain following fields:
 *   * `date` - either "default", "short", "long", or "full".
 *   * `startDate` - developer designated locale (this will take precedence over browser setting, if language differs).
 */
export const RelativeDate = ({
  date,
  startDate,
}: {
  date: Date;
  startDate?: Date;
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

  return <>{getRelativeFormattedDate(date, T, startDate)}</>;
};
